import { Component } from '@theme/component';
import { debounce, onDocumentReady } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 250;
const DEACTIVATE_DELAY = 350;

/**
 * Switching between top-level items used to be instant — activate() called
 * #activateHandler synchronously whenever something was already open. On
 * desktop that made the menu swap on any pointer that merely grazed a
 * neighbouring trigger.
 *
 * Measured Aug 2026 at 1440px: the triggers sit in a band at y 16-60, and
 * "Men" is only 26px wide (x 40-66) while "Women" starts 38px to its right at
 * x 104. The mega-menu panel opens at y 75 and, once open, every point in it
 * belongs to the open item — so the panel itself is safe. The whole problem is
 * that band: moving down-and-right out of a narrow trigger toward the panel
 * clips the next trigger for a few frames, and the menu swapped.
 *
 * 180ms rejects a pass-over (a fast diagonal clips a neighbour for roughly
 * 30-80ms) while a deliberate landing still feels immediate, since you dwell
 * there anyway. Deliberately below ACTIVATE_DELAY: opening from nothing is a
 * bigger commitment than moving along an already-open menu.
 *
 * Do not set this to 0. That is the bug.
 */
const SWITCH_DELAY = 180;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['submenu'];

  connectedCallback() {
    super.connectedCallback();

    onDocumentReady(this.#preloadImages);

    // Native listener, NOT an on:click binding: the declarative event
    // system proxies the event so target becomes the bound element (the
    // li) — a click on a submenu link inside the li then looks identical
    // to a click on the label, which both killed the link's navigation
    // and toggled the panel shut. A native listener sees the real target.
    this.addEventListener('click', this.toggle);

    document.addEventListener('click', this.#onDocumentClick);
    document.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    document.removeEventListener('click', this.#onDocumentClick);
    document.removeEventListener('keydown', this.#onKeydown);
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();

    if (this.#state.activeItem) {
      this.#debouncedSwitchHandler(event);
    } else {
      this.#debouncedActivateHandler(event);
    }
  };

  /**
   * FW26 (Roman, 9 Aug 2026): the desktop menu opens on CLICK, not hover.
   * Hover mode lost a three-front war against the pointer: the V2 16px
   * header padding put a dead strip between label and panel (pointerleave
   * -> 350ms close), the panel reveals via a 450ms clip-path transition
   * whose unrevealed region is not hit-testable (a fast descent hovered
   * the page through it), and narrow labels 38px apart made every arc
   * graze a sibling. Click has no timers to lose to. Top-level items WITH
   * children toggle their panel and never navigate (the panel's "All"
   * link covers that); items without children (Journal) navigate as
   * normal links. Bound as a NATIVE click listener in connectedCallback —
   * deliberately not an on:click binding (see the note there);
   * snippets/header-menu.liquid no longer binds pointerenter/pointerleave.
   * @param {PointerEvent | MouseEvent} event
   */
  toggle = (event) => {
    if (!(event.target instanceof Element)) return;

    // Clicks inside the open panel are real navigation — let them through.
    if (event.target.closest('[ref="submenu[]"]')) return;

    const li = event.target.closest('.menu-list__list-item');
    const item = findMenuItem(li);

    if (!item) return;

    // No submenu (e.g. Journal): behave as a plain link.
    if (!findSubmenu(item)) return;

    event.preventDefault();

    this.#debouncedActivateHandler.cancel();
    this.#debouncedSwitchHandler.cancel();
    this.#debouncedDeactivate.cancel();

    if (item === this.#state.activeItem) {
      this.#deactivate();
    } else {
      this.#openItem(item, li instanceof HTMLElement ? li.slot : '');
    }
  };

  /**
   * Close the menu when clicking anywhere outside it.
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    if (!this.#state.activeItem) return;
    if (event.target instanceof Element && this.contains(event.target)) return;
    this.#deactivate();
  };

  /**
   * Close the menu on Escape.
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key === 'Escape' && this.#state.activeItem) {
      this.#deactivate();
    }
  };

  /**
   * Open a specific menu item immediately.
   * @param {HTMLElement} item
   * @param {string} slot
   */
  #openItem = (item, slot = '') => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!item || item == this.#state.activeItem) return;

    this.dataset.overflowExpanded = (slot !== '').toString();

    this.#deactivate();
    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    const submenu = findSubmenu(item);

    if (!submenu) return;

    this.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    if (!(event.target instanceof Element)) return;

    this.#openItem(findMenuItem(event.target), event.target.slot ?? '');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  #debouncedSwitchHandler = debounce(this.#activateHandler, SWITCH_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();
    this.#debouncedSwitchHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate(event);
    }
  }

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce((event) => {
    this.dataset.overflowExpanded = 'false';

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item != this.#state.activeItem) return;

    this.#deactivate(item);
  }, DEACTIVATE_DELAY);

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    this.style.setProperty('--submenu-height', '0px');

    if (!item) return;

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, this.animationDelay);
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"]')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[role="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
